import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    login: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Use ref to track if redirection has already been performed
  const redirectionDone = useRef(false);

  useEffect(() => {
    // Prevent multiple redirections
    if (redirectionDone.current) return;

    // Check if already logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    try {
      // If we have both token and user data with isAdmin flag
      if (token && userData) {
        const user = JSON.parse(userData);

        // Only redirect if the user is an admin
        if (user.isAdmin) {
          // Set admin session flag
          localStorage.setItem('isAdminSession', 'true');
          redirectionDone.current = true;
          navigate('/admin');
        }
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      // Clear potentially corrupted data
      localStorage.removeItem('user');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, credentials);

      const { token, user } = response.data;

      if (!user.isAdmin) {
        toast.error('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      // IMPORTANT: Store the raw JWT token without any prefix
      // Remove any 'Bearer ' prefix if it exists
      const cleanToken = token.startsWith('Bearer ')
        ? token.substring(7).trim()
        : token.trim();

      localStorage.setItem('token', cleanToken);

      // Log token details for debugging
      console.log('Stored admin token:', {
        original: token.substring(0, 15) + '...',
        cleaned: cleanToken.substring(0, 15) + '...',
        length: cleanToken.length,
        isAdmin: user.isAdmin
      });

      // Verify the token has the admin flag
      if (!user.isAdmin) {
        console.error('Token does not have admin flag!');
      }

      // Update the user data in localStorage
      const userData = {
        ...user,
        token: cleanToken, // Include the clean token in user data
        isLoggedIn: true,
        isAdmin: true, // Ensure this is explicitly set
        loginTimestamp: Date.now(),
        // Add expiration timestamp for 7 days (matching the server-side setting)
        // This ensures the admin login remains persistent for a long time
        expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000),
        // Add a separate session expiration for 4 hours
        sessionExpiresAt: Date.now() + (4 * 60 * 60 * 1000)
      };
      localStorage.setItem('user', JSON.stringify(userData));

      // Add debug info to console
      console.log('Admin login successful:', {
        token: token.substring(0, 20) + '...',
        user: userData,
        expires: new Date(userData.expiresAt).toLocaleString()
      });

      // Set flag to indicate admin session to prevent unnecessary wallet API calls
      localStorage.setItem('isAdminSession', 'true');

      toast.success('Login successful!');
      navigate('/admin');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-white">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="BitSport Logo" className="h-12 mx-auto mb-4" />
            <h1 className="text-3xl font-bold">Admin Login</h1>
            <p className="text-gray-400 mt-2">Enter your credentials to access the admin panel</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="login" className="block text-sm font-medium text-gray-300 mb-1">
                Email or Username
              </label>
              <input
                id="login"
                name="login"
                type="text"
                value={credentials.login}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="Enter your email or username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={credentials.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg font-medium text-white hover:from-violet-700 hover:to-purple-700 transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          {/* Admin credentials section removed */}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
